<template>
  <div class="meeting-list-page">
    <SubPageHeader title="三会一课" />
    <div class="content">
      <div v-if="isLoading" class="state-text">加载中...</div>
      <div v-else-if="meetingList.length === 0" class="state-text">暂无三会一课数据</div>
      <div v-else class="list">
        <div
          v-for="(item, index) in meetingList"
          :key="item.id || item.meeting_id || item.record_id || index"
          class="meeting-card"
        >
          <div class="card-row title-row">
            <span class="label">会议标题：</span>
            <span class="value">{{ getTitle(item) }}</span>
          </div>
          <div class="card-row">
            <span class="label">会议类型：</span>
            <span class="value">{{ getMeetingType(item) }}</span>
          </div>
          <div class="card-row">
            <span class="label">开始时间：</span>
            <span class="value">{{ getStartTime(item) }}</span>
          </div>
          <div class="card-row">
            <span class="label">结束时间：</span>
            <span class="value">{{ getEndTime(item) }}</span>
          </div>
          <div class="card-row">
            <span class="label">会议状态：</span>
            <span :class="['status-tag', getStatusClass(item)]">{{ getStatusName(item) }}</span>
          </div>
          <div class="action-row">
            <button
              v-if="canJoinMeeting(item)"
              class="action-btn primary-btn"
              type="button"
              @click="goMetaverseMeetingHall"
            >
              一键入会
            </button>
            <button
              class="action-btn report-btn"
              type="button"
              :disabled="!canViewMeetingReport(item)"
              @click="goMeetingReportDetail(item)"
            >
              会议纪要
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMeetingList } from '@/api';
import { getToken } from '@/utils/auth';
import SubPageHeader from '../component/SubPageHeader.vue';

const META_BASE_URL_MAP = {
  test: 'http://www.dangjian.com',
  prod: 'https://aidj.gee.cn'
};

const STATUS_CLASS_MAP = {
  2: 'status-finished',
  1: 'status-processing',
  0: 'status-pending'
};

export default {
  name: 'MeetingListPage',
  components: {
    SubPageHeader
  },
  data() {
    return {
      isLoading: false,
      meetingList: []
    };
  },
  mounted() {
    this.fetchMeetingList();
  },
  methods: {
    async fetchMeetingList() {
      this.isLoading = true;
      try {
        const res = await getMeetingList();
        this.meetingList = this.normalizeList(res?.data);
      } catch (error) {
        this.meetingList = [];
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.restoreScrollPosition();
        });
      }
    },
    getScrollPosition() {
      const appEl = document.getElementById('app');
      return appEl ? appEl.scrollTop : 0;
    },
    setScrollPosition(y) {
      const appEl = document.getElementById('app');
      if (appEl) {
        appEl.scrollTop = y;
      }
    },
    restoreScrollPosition() {
      const scrollY = sessionStorage.getItem('meetingListPageScrollY');
      if (!scrollY) return;
      
      const targetY = parseInt(scrollY, 10);
      if (targetY > 0) {
        this.setScrollPosition(targetY);
        sessionStorage.removeItem('meetingListPageScrollY');
      }
    },
    normalizeList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (Array.isArray(payload?.list)) {
        return payload.list;
      }
      if (Array.isArray(payload?.rows)) {
        return payload.rows;
      }
      return [];
    },
    getTitle(item) {
      return item?.title || item?.meeting_title || item?.name || '三会一课';
    },
    getMeetingType(item) {
      return item?.meeting_type_name || item?.meeting_type || item?.type_name || '--';
    },
    getStartTime(item) {
      return item?.start_time || '--';
    },
    getEndTime(item) {
      return item?.end_time || '--';
    },
    getStatusCode(item) {
      const status = Number(item?.status);
      if (Number.isFinite(status)) {
        return status;
      }
      return -1;
    },
    getStatusName(item) {
      return item?.status_name || this.getFallbackStatusName(this.getStatusCode(item));
    },
    getFallbackStatusName(status) {
      if (status === 0) {
        return '待开始';
      }
      if (status === 1) {
        return '进行中';
      }
      if (status === 2) {
        return '已结束';
      }
      return '--';
    },
    getStatusClass(item) {
      const status = this.getStatusCode(item);
      return STATUS_CLASS_MAP[status] || 'status-default';
    },
    canJoinMeeting(item) {
      const status = this.getStatusCode(item);
      return status === 0 || status === 1;
    },
    canViewMeetingReport(item) {
      return this.getStatusCode(item) === 2;
    },
    getMetaBaseUrl() {
      const env = process.env.VUE_APP_API_ENV || 'local';
      return META_BASE_URL_MAP[env] || META_BASE_URL_MAP.prod;
    },
    goMetaverseMeetingHall() {
      const token = getToken();
      window.location.href = `${this.getMetaBaseUrl()}/meta/meta2/select_role.html?access-token=${encodeURIComponent(token)}&meeting=1`;
    },
    goMeetingReportDetail(item) {
      const meetingId = item?.id || item?.meeting_id || item?.aid;
      if (!meetingId) {
        return;
      }
      
      // 记录当前滑动位置
      const scrollY = this.getScrollPosition();
      sessionStorage.setItem('meetingListPageScrollY', scrollY);

      this.$router.push({
        path: '/organize/meeting/report',
        query: {
          meetingId: String(meetingId)
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.meeting-list-page {
  min-height: 100vh;
  background: #ffffff;
}

.content {
  padding: 3.58974vw 3.58974vw 5.12821vw;
}

.state-text {
  padding: 10.25641vw 0;
  text-align: center;
  color: #999;
  font-size: 3.84615vw;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 3.07692vw;
}

.meeting-card {
  position: relative;
  background: #ffffff;
  border-radius: 3.58974vw;
  padding: 4.10256vw 3.58974vw 3.58974vw;
  box-shadow: 0 1.4vw 4.2vw rgba(174, 49, 49, 0.12);
  border: 0.25641vw solid #ffe3e3;
}

.card-row {
  margin-top: 1.53846vw;
  display: flex;
  align-items: flex-start;
  font-size: 3.48718vw;
  line-height: 1.5;
}

.card-row:first-child {
  margin-top: 0;
}

.title-row .value {
  font-size: 4.10256vw;
  font-weight: 700;
}

.label {
  width: 18vw;
  flex-shrink: 0;
  color: #a48484;
}

.value {
  color: #3d2a2a;
  font-weight: 500;
  word-break: break-all;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14.87179vw;
  height: 6.15385vw;
  padding: 0 2.30769vw;
  border-radius: 3.07692vw;
  font-size: 3.07692vw;
  font-weight: 600;
  line-height: 1;
  border: 0.25641vw solid transparent;
}

.status-pending {
  color: #be7b13;
  background: #fff6e6;
  border-color: #f4d4a0;
}

.status-processing {
  color: #2562d4;
  background: #edf3ff;
  border-color: #c3d4ff;
}

.status-finished {
  color: #2f955d;
  background: #ecf9f1;
  border-color: #b8e3c9;
}

.status-default {
  color: #6a7282;
  background: #f4f6fa;
  border-color: #e3e8f2;
}

.action-row {
  margin-top: 3.07692vw;
  display: flex;
  justify-content: flex-end;
  gap: 2.05128vw;
}

.action-btn {
  border: none;
  outline: none;
  height: 8.71795vw;
  border-radius: 4.35897vw;
  padding: 0 4.10256vw;
  font-size: 3.33333vw;
  font-weight: 600;
  cursor: pointer;
}

.primary-btn {
  color: #ffffff;
  background: linear-gradient(90deg, #ff6e6e 0%, #ff4f4f 100%);
}

.report-btn {
  color: #d64545;
  background: #fff3f3;
  border: 0.25641vw solid #f7c0c0;
}

.report-btn:disabled {
  color: #a7adb7;
  background: #f3f5f8;
  border-color: #d9dfe8;
  cursor: not-allowed;
}
</style>
